import React, { useState, useCallback } from 'react';
import { checkGiftCodeEligibility, applyGiftCodeUsage } from '../../services/giftCodeService';
import { addMyQuizSelection } from '../../services/myQuizSelectionService';
import { useMyQuizSelection } from '../../hooks/useMyQuizSelection';
import { logger } from '../../utils/logger';
import { fetchQuizById } from '../../services/quizService';
import { useOnlineStatus } from '../../hooks/useOnlineStatus'; // 　オンライン判定用

interface GiftCodeEntryViewProps {
    userId: string;
    handlePageChange: (page: string) => void;
}

const GiftCodeEntryView: React.FC<GiftCodeEntryViewProps> = ({ userId, handlePageChange }) => {
    const [giftCode, setGiftCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | null }>({ text: '', type: null });

    const isOnline = useOnlineStatus(); // オンライン状態を取得

    const { myQuizSelection, mutate: myQuizMute } = useMyQuizSelection(userId);

    // 検索結果に表示されているクイズがMyQuizSelectionに追加済みかどうかを判定するヘルパー関数
    const isQuizAddedToMySelection = useCallback((quizId: string): boolean => {
        return myQuizSelection?.quizSummaries?.some(summary => summary.id === quizId) || false;
    }, [myQuizSelection]);

    const handleAddQuizFromGiftCode = async () => {
        // オフラインの場合は処理を実行しない (念のためのガード)
        if (!isOnline) {
            setMessage({ text: 'オフラインのため、操作できません。', type: 'error' });
            return;
        }

        if (!giftCode.trim()) {
            setMessage({ text: 'ギフトコードを入力してください。', type: 'error' });
            return;
        }
        setLoading(true);
        setMessage({ text: '', type: null });

        try {
            // 1. ギフトコードの有効性をチェックし、紐づくクイズIDを取得
            const giftCodeInfo = await checkGiftCodeEligibility(giftCode, userId);
            if (!giftCodeInfo) {
                setMessage({ text: '無効なギフトコードです。', type: 'error' });
                return;
            }
            // 2. クイズがすでにMyQuizSelectionに追加済みかチェック
            if (isQuizAddedToMySelection(giftCodeInfo.QuizId)) {
                setMessage({ text: 'このギフトコードで紐づくクイズはすでに追加済みです。', type: 'error' });
                return
            }

            // 3. クイズIDからクイズデータを取得
            const quizData = await fetchQuizById(giftCodeInfo.QuizId);
            if (!quizData) {
                setMessage({ text: 'ギフトコードに紐づくクイズが見つかりませんでした。', type: 'error' });
                return;
            }

            // 4. クイズを追加
            await addMyQuizSelection(userId, quizData, true);

            // 5. SWRキャッシュを再検証してUIを更新
            myQuizMute();
            setMessage({ text: 'ギフトコードでクイズが正常に追加されました！', type: 'success' });

            //6. ギフトコードの使用状況を更新
            await applyGiftCodeUsage(giftCode, userId);


        } catch (err: any) {
            logger.error('ギフトコードでのクイズ追加に失敗しました:', err);
            setMessage({ text: 'エラーが発生しました: ' + err.message, type: 'error' });
        } finally {
            setLoading(false);
            setGiftCode(''); // 入力フィールドをクリア
        }
    };

    return (
        <div className="p-6 max-w-xl mx-auto min-h-screen flex flex-col justify-center">
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200">
                <h2 className="text-3xl font-extrabold text-center text-gray-800 mb-6">
                    ギフトコードでクイズを追加
                </h2>
                <p className="text-center text-gray-600 mb-8">
                    お手持ちのギフトコードを入力して、新しいクイズを始めましょう！
                </p>

                {/*  オフライン時にメッセージを表示 */}
                {!isOnline && (
                    <div className="mb-6 p-4 rounded-lg font-semibold text-center bg-yellow-100 text-yellow-800 border border-yellow-200">
                        <p>現在オフラインです。</p>
                        <p>この機能はオンライン時にご利用いただけます。</p>
                    </div>
                )}

                <div className="flex flex-col space-y-4">
                    <input
                        type="text"
                        placeholder="ギフトコードを入力してください"
                        value={giftCode}
                        onChange={(e) => setGiftCode(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !loading && isOnline) {
                                handleAddQuizFromGiftCode();
                            }
                        }}
                        className="p-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-lg"
                        disabled={loading || !isOnline} 
                    />
                    <button
                        onClick={handleAddQuizFromGiftCode}
                        disabled={loading || !giftCode.trim() || !isOnline}
                        className="w-full py-4 bg-blue-500 text-white font-bold text-lg rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? '追加中...' : 'クイズを追加'}
                    </button>
                </div>

                {message.text && (
                    <div className={`mt-6 p-4 rounded-lg font-semibold text-center ${message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {message.text}
                    </div>
                )}

                <div className="mt-8 text-center">
                    <button
                        onClick={() => handlePageChange('home')}
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                    >
                        ホームへ
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GiftCodeEntryView;